import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { extractError } from "../../lib/format";
import { Loader2, UserPlus, FileText, Award } from "lucide-react";

const ICON_BY_TYPE = {
  signup: UserPlus,
  submission: FileText,
  certificate: Award,
};

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminOverview() {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    api.get("/admin/overview")
      .then(({ data }) => alive && setState({ loading: false, data, error: null }))
      .catch((e) => alive && setState({ loading: false, data: null, error: extractError(e, "Could not load overview.") }));
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    return <div className="ftg-adm__empty" data-testid="overview-loading"><Loader2 className="ftg-spin" size={18} /> Loading overview…</div>;
  }
  if (state.error) {
    return <div className="ftg-adm__empty" data-testid="overview-error">{state.error}</div>;
  }
  const { counts, enrollments_by_band, recent } = state.data;
  const cards = [
    { label: "Users",              value: counts.users_total, testid: "stat-users" },
    { label: "Enrollments",        value: counts.enrollments_total, testid: "stat-enrollments" },
    { label: "Submissions today",  value: counts.submissions_today, testid: "stat-subs-today" },
    { label: "Submissions this week", value: counts.submissions_week, testid: "stat-subs-week" },
    { label: "Total submissions",  value: counts.submissions_total, testid: "stat-subs-total", muted: true },
    { label: "Flagged submissions", value: counts.submissions_flagged, testid: "stat-flagged", accent: counts.submissions_flagged ? "warn" : null },
    { label: "Level 1 certs",      value: counts.certificates_level_1, testid: "stat-l1" },
    { label: "Level 2 certs",      value: counts.certificates_level_2, testid: "stat-l2" },
    { label: "Waitlist",           value: counts.waitlist_total, testid: "stat-waitlist" },
  ];

  return (
    <div className="ftg-adm__stack" data-testid="overview-root">
      <section className="ftg-adm__stat-grid" data-testid="overview-stats">
        {cards.map((c) => (
          <div key={c.testid} className={`ftg-adm__stat${c.accent === "warn" ? " is-warn" : ""}${c.muted ? " is-muted" : ""}`} data-testid={c.testid}>
            <div className="ftg-adm__stat-label">{c.label}</div>
            <div className="ftg-adm__stat-value">{c.value}</div>
          </div>
        ))}
      </section>

      <section>
        <h3 className="ftg-adm__section-title">Enrollments by age band</h3>
        <div className="ftg-adm__bar-list" data-testid="enroll-by-band">
          {enrollments_by_band.map((b) => {
            const max = Math.max(1, ...enrollments_by_band.map((x) => x.count));
            const pct = Math.round((b.count / max) * 100);
            return (
              <div key={b.age_band} className="ftg-adm__bar-row" data-testid={`bar-${b.age_band}`}>
                <div className="ftg-adm__bar-label">{b.label}</div>
                <div className="ftg-adm__bar-track">
                  <div className="ftg-adm__bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="ftg-adm__bar-count">{b.count}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="ftg-adm__section-title">Recent activity</h3>
        <div className="ftg-adm__feed" data-testid="recent-feed">
          {!recent.length && <div className="ftg-adm__empty">No activity yet.</div>}
          {recent.map((r, i) => {
            const Icon = ICON_BY_TYPE[r.type] || FileText;
            return (
              <div key={i} className={`ftg-adm__feed-row ftg-adm__feed-row--${r.type}`} data-testid={`feed-row-${i}`}>
                <div className="ftg-adm__feed-icon"><Icon size={14} /></div>
                <div className="ftg-adm__feed-body">
                  <div className="ftg-adm__feed-label">{r.label}</div>
                  <div className="ftg-adm__feed-detail">{r.detail}</div>
                </div>
                <div className="ftg-adm__feed-time">{formatWhen(r.at)}</div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
